import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Skeleton,
} from "@mui/material";
import {
  Check as CheckIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  SupportAgent as SupportAgentIcon,
  ShoppingBag as BagIcon,
  Favorite as WishlistIcon,
  ShoppingCart as OrdersIcon,
  ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  CalendarTodayOutlined as CalendarTodayOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";
import { PropagateLoader } from "react-spinners";
import config from "../../Config";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
  success: "#4E6B50",
  warning: "#9A6A22",
  blue: "#536B83",
};

const SidebarItem = ({ icon, label, active, onClick }) => (
  <ListItem
    component="button"
    onClick={onClick}
    sx={{
      width: "100%",
      border: 0,
      outline: "none",
      textAlign: "left",
      background: active ? COLORS.black : "transparent",
      color: active ? COLORS.white : COLORS.mutedBlack,
      borderRadius: 0,
      px: 2,
      py: 1.6,
      mb: 0.6,
      cursor: "pointer",
      transition: "all 0.25s ease",
      "&:hover": {
        background: active ? COLORS.black : COLORS.cream,
        color: active ? COLORS.white : COLORS.black,
        transform: "translateX(3px)",
      },
      "& .MuiListItemIcon-root": {
        minWidth: 40,
        color: active ? COLORS.goldLight : COLORS.grey,
        transition: "color 0.25s ease",
      },
    }}
  >
    <ListItemIcon>{icon}</ListItemIcon>

    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        letterSpacing: "0.02em",
      }}
    />

    <ArrowForwardIosRoundedIcon
      sx={{
        fontSize: 12,
        opacity: active ? 0.9 : 0,
        transform: active ? "translateX(0)" : "translateX(-5px)",
        transition: "all 0.25s ease",
      }}
    />
  </ListItem>
);

const QontoConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 10,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.border,
    borderLeftWidth: 1,
    minHeight: 48,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.gold,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.gold,
  },
}));

const QontoStepIconRoot = styled("div")(({ ownerState }) => ({
  color: COLORS.border,
  display: "flex",
  height: 22,
  alignItems: "center",
  justifyContent: "center",
  ...(ownerState.active && {
    color: COLORS.gold,
  }),
  "& .QontoStepIcon-completedIcon": {
    color: COLORS.gold,
    zIndex: 1,
    fontSize: 19,
  },
  "& .QontoStepIcon-circle": {
    width: 9,
    height: 9,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckIcon className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

const LoadingState = () => (
  <Box
    sx={{
      minHeight: "70vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: COLORS.cream,
    }}
  >
    <PropagateLoader color={COLORS.gold} size={10} />
  </Box>
);

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = sessionStorage.getItem("userId");

  const fetchOrderDetails = useCallback(async () => {
    if (!userId) {
      navigate("/Signin");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/${config.getOrderDetails}`,
        {
          headers: {
            "booking-id": orderId,
          },
        },
      );

      setOrderData(response.data?.OrderDetail?.bookingData || null);
    } catch (err) {
      setError(err.message || "Failed to fetch order details.");
    } finally {
      setLoading(false);
    }
  }, [navigate, orderId, userId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const openAppRedirect = () => {
    const appPackage = "com.ytctechventures.hindustanwardrobe";
    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = `https://play.google.com/store/apps/details?id=${appPackage}`;
    }, 1200);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <LoadingState />
        <Footer />
      </>
    );
  }

  if (error || !orderData) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "70vh",
            backgroundColor: COLORS.cream,
            py: 8,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                maxWidth: 700,
                mx: "auto",
                textAlign: "center",
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                p: { xs: 4, md: 7 },
              }}
            >
              <ReceiptLongOutlinedIcon
                sx={{
                  fontSize: 48,
                  color: COLORS.gold,
                  mb: 2,
                }}
              />

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 32,
                  color: COLORS.black,
                  mb: 1,
                }}
              >
                Order not found
              </Typography>

              <Typography
                sx={{
                  color: COLORS.grey,
                  fontSize: 14,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                {error || "We could not find the requested order details."}
              </Typography>

              <Box
                component="button"
                onClick={() => navigate("/orders")}
                sx={{
                  border: `1px solid ${COLORS.black}`,
                  backgroundColor: COLORS.black,
                  color: COLORS.white,
                  px: 3.5,
                  py: 1.5,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    backgroundColor: COLORS.gold,
                    borderColor: COLORS.gold,
                  },
                }}
              >
                Back to Orders
              </Box>
            </Box>
          </Container>
        </Box>

        <Footer />
      </>
    );
  }

  const currentStatusRaw = orderData.bookingStatus;
  const currentStatus =
    currentStatusRaw === "Order is Placed" ? "Placed" : "Confirmed";

  const product = orderData.productData?.product;
  const productImage = orderData.productData?.productImage?.[0];

  const statusStyles =
    currentStatus === "Confirmed"
      ? {
          color: COLORS.success,
          backgroundColor: "#EAF1EA",
          borderColor: "#C9D8C9",
        }
      : {
          color: COLORS.warning,
          backgroundColor: "#F7F0E3",
          borderColor: "#E6D3AD",
        };

  const address = orderData.deliveryAddress;

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: COLORS.cream,
          minHeight: "100vh",
          py: { xs: 4, md: 7 },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 0, md: 5 },
              alignItems: "start",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: {
                  xs: "none",
                  sm: "none",
                  md: "block",
                },
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.white,
                borderRadius: 0,
                overflow: "hidden",
                position: "sticky",
                top: 100,
              }}
            >
              <Box
                sx={{
                  backgroundColor: COLORS.black,
                  color: COLORS.white,
                  p: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: COLORS.goldLight,
                    mb: 0.7,
                  }}
                >
                  Account
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 24,
                    lineHeight: 1.2,
                  }}
                >
                  My Wardrobe
                </Typography>
              </Box>

              <List sx={{ p: 1.2 }}>
                <SidebarItem
                  icon={<PersonIcon />}
                  label="Profile"
                  onClick={() => navigate("/profile")}
                />

                <SidebarItem
                  icon={<BagIcon />}
                  label="Booking Request"
                  onClick={openAppRedirect}
                />

                <SidebarItem
                  icon={<OrdersIcon />}
                  label="My Orders"
                  active
                  onClick={() => navigate("/orders")}
                />

                <SidebarItem
                  icon={<WishlistIcon />}
                  label="Wishlist"
                  onClick={() => navigate("/wishlist")}
                />

                <SidebarItem
                  icon={<SupportAgentIcon />}
                  label="Help & Support"
                  onClick={() => navigate("/contact-us")}
                />
              </List>
            </Paper>

            <Box sx={{ minWidth: 0 }}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{
                  mb: 4,
                  "& .MuiBreadcrumbs-separator": {
                    color: COLORS.lightGrey,
                  },
                }}
              >
                <Link
                  component="button"
                  underline="none"
                  onClick={() => navigate("/orders")}
                  sx={{
                    border: 0,
                    background: "transparent",
                    padding: 0,
                    color: COLORS.grey,
                    fontSize: 11,
                    cursor: "pointer",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    "&:hover": {
                      color: COLORS.black,
                    },
                  }}
                >
                  Orders
                </Link>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: COLORS.black,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Order Details
                </Typography>
              </Breadcrumbs>

              <Box
                sx={{
                  mb: { xs: 4, md: 5 },
                  borderBottom: `1px solid ${COLORS.border}`,
                  pb: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: COLORS.gold,
                    mb: 1,
                  }}
                >
                  Order overview
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 34,
                      sm: 42,
                      md: 52,
                    },
                    fontWeight: 400,
                    lineHeight: 1.08,
                    color: COLORS.black,
                    mb: 1.5,
                  }}
                >
                  Order Details
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.grey,
                    fontSize: 14,
                    lineHeight: 1.8,
                  }}
                >
                  Everything you need to know about your wardrobe rental.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      overflow: "hidden",
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "minmax(0, 1fr) 260px",
                        },
                      }}
                    >
                      <Box sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ mb: 3 }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              textTransform: "uppercase",
                              letterSpacing: "0.16em",
                              color: COLORS.gold,
                            }}
                          >
                            Rental Item
                          </Typography>

                          <Chip
                            label={currentStatus}
                            size="small"
                            sx={{
                              height: 28,
                              borderRadius: 0,
                              border: `1px solid ${statusStyles.borderColor}`,
                              color: statusStyles.color,
                              backgroundColor: statusStyles.backgroundColor,
                              fontSize: 10,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          />
                        </Stack>

                        <Typography
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontSize: {
                              xs: 28,
                              md: 34,
                            },
                            lineHeight: 1.15,
                            color: COLORS.black,
                            mb: 2.5,
                            textTransform: "capitalize",
                          }}
                        >
                          {product?.title || "Wardrobe Item"}
                        </Typography>

                        <Divider
                          sx={{
                            borderColor: COLORS.border,
                            mb: 2.5,
                          }}
                        />

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr 1fr",
                              sm: "1fr 1fr",
                            },
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: COLORS.grey,
                                textTransform: "uppercase",
                                letterSpacing: "0.13em",
                                mb: 0.5,
                              }}
                            >
                              Order ID
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 12,
                                color: COLORS.black,
                                wordBreak: "break-word",
                              }}
                            >
                              {orderData.bookingId}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: COLORS.grey,
                                textTransform: "uppercase",
                                letterSpacing: "0.13em",
                                mb: 0.5,
                              }}
                            >
                              Rental Price
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 20,
                                fontWeight: 600,
                                color: COLORS.black,
                              }}
                            >
                              ₹{product?.rentAmount ?? 0}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: COLORS.grey,
                                textTransform: "uppercase",
                                letterSpacing: "0.13em",
                                mb: 0.5,
                              }}
                            >
                              Booking Date
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={0.8}
                              alignItems="center"
                            >
                              <CalendarTodayOutlinedIcon
                                sx={{
                                  fontSize: 14,
                                  color: COLORS.gold,
                                }}
                              />

                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: COLORS.black,
                                }}
                              >
                                {orderData.bookingDate || "—"}
                              </Typography>
                            </Stack>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: 9,
                                color: COLORS.grey,
                                textTransform: "uppercase",
                                letterSpacing: "0.13em",
                                mb: 0.5,
                              }}
                            >
                              Status
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: statusStyles.color,
                              }}
                            >
                              {currentStatus}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: COLORS.softCream,
                          minHeight: {
                            xs: 280,
                            sm: 320,
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={
                            productImage ||
                            "https://via.placeholder.com/500x600"
                          }
                          alt={product?.title || "Product"}
                          sx={{
                            width: "100%",
                            height: "100%",
                            minHeight: {
                              xs: 280,
                              sm: 320,
                            },
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 3, md: 4 },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.2}
                      sx={{ mb: 3 }}
                    >
                      <LocationOnOutlinedIcon
                        sx={{
                          color: COLORS.gold,
                          fontSize: 21,
                        }}
                      />

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 25,
                          color: COLORS.black,
                        }}
                      >
                        Delivery Address
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        backgroundColor: COLORS.softCream,
                        border: `1px solid ${COLORS.border}`,
                        p: { xs: 2.5, md: 3 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: COLORS.mutedBlack,
                          lineHeight: 2,
                        }}
                      >
                        {address?.street1 || ""}
                        {address?.street2 ? `, ${address.street2}` : ""}
                        <br />

                        {address?.city && `${address.city}, `}
                        {address?.state && `${address.state}, `}
                        {address?.country || ""}
                        <br />

                        {address?.pinCode ? `PIN: ${address.pinCode}` : ""}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 3, md: 4 },
                      height: "100%",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.2}
                      sx={{ mb: 3.5 }}
                    >
                      <Inventory2OutlinedIcon
                        sx={{
                          color: COLORS.gold,
                          fontSize: 21,
                        }}
                      />

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 25,
                          color: COLORS.black,
                        }}
                      >
                        Order Timeline
                      </Typography>
                    </Stack>

                    <Stepper
                      orientation="vertical"
                      connector={<QontoConnector />}
                      sx={{
                        "& .MuiStepLabel-labelContainer": {
                          py: 0.2,
                        },
                        "& .MuiStep-root": {
                          pb: 1,
                        },
                      }}
                    >
                      <Step completed>
                        <StepLabel StepIconComponent={QontoStepIcon}>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: COLORS.black,
                              }}
                            >
                              Order Placed
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11,
                                color: COLORS.grey,
                                mt: 0.4,
                              }}
                            >
                              Your rental request has been received.
                            </Typography>
                          </Box>
                        </StepLabel>
                      </Step>

                      <Step
                        completed={currentStatus === "Confirmed"}
                        active={currentStatus === "Confirmed"}
                      >
                        <StepLabel StepIconComponent={QontoStepIcon}>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: COLORS.black,
                              }}
                            >
                              Order Confirmed
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11,
                                color: COLORS.grey,
                                mt: 0.4,
                              }}
                            >
                              {currentStatus === "Confirmed"
                                ? "Your order has been confirmed."
                                : "Waiting for confirmation."}
                            </Typography>
                          </Box>
                        </StepLabel>
                      </Step>
                    </Stepper>

                    <Divider
                      sx={{
                        borderColor: COLORS.border,
                        my: 3,
                      }}
                    />

                    <Box
                      sx={{
                        backgroundColor: COLORS.black,
                        color: COLORS.white,
                        p: 2.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          textTransform: "uppercase",
                          letterSpacing: "0.16em",
                          color: COLORS.goldLight,
                          mb: 0.7,
                        }}
                      >
                        Current Status
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 22,
                        }}
                      >
                        {currentStatus}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 3,
                  borderTop: `1px solid ${COLORS.border}`,
                  pt: 3,
                }}
              >
                <Box
                  component="button"
                  onClick={() => navigate("/orders")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    border: 0,
                    background: "transparent",
                    color: COLORS.mutedBlack,
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    cursor: "pointer",
                    p: 0,
                    transition: "all 0.25s ease",
                    "&:hover": {
                      color: COLORS.gold,
                      transform: "translateX(-3px)",
                    },
                  }}
                >
                  ← Back to My Orders
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default OrderDetails;
